import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import PaginationComponent from "../../../Components/PaginationComponent";
import saleService from "../../../Services/saleService";

const PatientSalesAll = () => {
  const [searchData, setSearchData] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getPSales = async () => {
      try {
        setLoading(true);
        const response = await saleService.fetchAllPSales();

        const sortedSales = response.sales.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        setPatients(sortedSales);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching sales:", error);
      }
    };

    getPSales();
  }, []);

  useEffect(() => {
    const filteredResult =
      patients?.filter((item) => {
        const itemDate = new Date(item?.date);

        const normalizedItemDate = new Date(
          itemDate.getFullYear(),
          itemDate.getMonth(),
          itemDate.getDate()
        );

        const normalizedStartDate = startDate
          ? new Date(new Date(startDate).setHours(0, 0, 0, 0))
          : null;

        const normalizedEndDate = endDate
          ? new Date(new Date(endDate).setHours(23, 59, 59, 999))
          : null;

        const isWithinDateRange =
          (!normalizedStartDate || normalizedStartDate <= normalizedItemDate) &&
          (!normalizedEndDate || normalizedItemDate <= normalizedEndDate);

        const matchesSearch =
          item?.full_name?.toLowerCase().includes(searchData.toLowerCase()) ||
          item?.receptionist_name?.toLowerCase().includes(searchData.toLowerCase()) ||
          item?.name?.toLowerCase().includes(searchData.toLowerCase());

        return matchesSearch && isWithinDateRange;
      }) || [];

    setFilteredData(filteredResult);
    setCurrentPage(1);
  }, [searchData, startDate, endDate, patients]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    setPaginatedData(filteredData?.slice(startIndex, endIndex) || []);
  }, [currentPage, filteredData]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="p-3 sm:p-6">

      <div className="bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-4 mb-4 sm:mb-5 shadow-sm">

  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

    <div>
      <h1 className="text-[15px] sm:text-[17px] font-[500] text-gray-800">
        Patient Sales Record
      </h1>

      <p className="text-[11px] sm:text-[12px] text-gray-400 mt-0.5">
        {filteredData?.length || 0} records found
      </p>
    </div>

    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">

      <input
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        className="w-full sm:w-auto text-[12px] sm:text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[6px]"
      />

      <input
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        className="w-full sm:w-auto text-[12px] sm:text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[6px]"
      />

      {(startDate || endDate) && (
        <button
          onClick={() => {
            setStartDate("");
            setEndDate("");
          }}
          className="w-full sm:w-auto text-[12px] sm:text-[13px] bg-gray-100 hover:bg-gray-200 px-3 py-[6px] rounded-lg"
        >
          Clear
        </button>
      )}

      <div className="relative w-full sm:w-[220px]">
        <SearchIcon
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
          style={{ fontSize: 16 }}
        />

        <input
          type="search"
          placeholder="Search..."
          value={searchData}
          onChange={(e) => setSearchData(e.target.value)}
          className="pl-8 pr-3 py-[6px] w-full text-[13px] bg-gray-50 border border-gray-200 rounded-lg"
        />
      </div>

    </div>
  </div>
</div>

      <div className="hidden sm:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">

        <div className="overflow-x-auto">

          <table className="w-full min-w-[720px]">

            <thead>
              <tr className="bg-[#2F6DB4] text-[11px] uppercase text-white">
                <th className="py-3 px-4 text-left">Invoice</th>
                <th className="py-3 px-4 text-center">Date</th>
                <th className="py-3 px-4 text-center">Time</th>
                <th className="py-3 px-4 text-left">Patient</th>
                <th className="py-3 px-4 text-left">Doctor</th>
                <th className="py-3 px-4 text-center">Amount</th>
                <th className="py-3 px-4 text-center">Receptionist</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-400">
                    No records found
                  </td>
                </tr>
              ) : (
                paginatedData.map((patient) => (
                  <tr key={patient.sale_id} className="border-t hover:bg-gray-50 text-[13px]">

                    <td className="py-3 px-4 text-gray-500">
                      #{patient.sale_id}
                    </td>

                    <td className="py-3 px-4 text-center">
                      {formatDate(patient.date)}
                    </td>

                    <td className="py-3 px-4 text-center">
                      {formatTime(patient.created_at)}
                    </td>

                    <td className="py-3 px-4 font-[500]">
                      {patient.full_name}
                    </td>

                    <td className="py-3 px-4">
                      {patient.name}
                    </td>

                    <td className="py-3 px-4 text-center">
                      {patient.fee}
                    </td>

                    <td className="py-3 px-4 text-center">
                      {patient.receptionist_name}
                    </td>

                  </tr>
                ))
              )}
            </tbody>

          </table>
        </div>
      </div>


      <div className="sm:hidden space-y-3">

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-gray-400">
            Loading...
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-gray-400">
            No records found
          </div>
        ) : (
          paginatedData.map((patient) => (

            <div
              key={patient.sale_id}
              className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
            >

              <div className="flex justify-between mb-3">

                <div>
                  <p className="text-[10px] font-bold text-[#2F6DB4] uppercase">
                    Invoice #{patient.sale_id}
                  </p>

                  <p className="text-[14px] font-semibold text-gray-800">
                    {patient.full_name}
                  </p>
                </div>

                <span className="text-[12px] font-medium text-gray-600">
                  Rs {patient.fee}
                </span>

              </div>

              <div className="grid grid-cols-2 gap-2 text-[12px]">

                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-400 text-[10px]">Doctor</p>
                  <p className="font-semibold">{patient.name}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-400 text-[10px]">Receptionist</p>
                  <p className="font-semibold">{patient.receptionist_name}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-400 text-[10px]">Date</p>
                  <p className="font-semibold">{formatDate(patient.date)}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-400 text-[10px]">Time</p>
                  <p className="font-semibold">{formatTime(patient.created_at)}</p>
                </div>

              </div>

            </div>

          ))
        )}

      </div>

      <PaginationComponent
        filteredData={filteredData}
        setPaginatedData={setPaginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />

    </div>
  );
};

export default PatientSalesAll;